import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, MessageCircle, Repeat, Layers, Heart, Handshake, ShieldCheck, ArrowRight, Sparkles } from "lucide-react";

function HoCercoScambio() {
  const steps = [
    { tag: "Ho", color: "#7C3AED", label: "Charizard ex", sub: "Near Mint · IT" },
    { tag: "Cerco", color: "#2DD4BF", label: "Pikachu", sub: "Excellent+ · qualsiasi" },
    { tag: "Scambio", color: "#10B981", label: "Corrispondenza", sub: "carta ↔ carta" },
  ];
  return (
    <div className="relative rounded-2xl border border-[#2A3655] bg-[#141B2D] p-5" data-testid="hero-illustration">
      <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full border border-[#2A3655] bg-[#1C2640] px-2 py-0.5 text-[10px] text-[#94A3B8]">
        <Sparkles className="h-3 w-3 text-[#2DD4BF]" /> Esempio dimostrativo
      </span>
      <div className="grid grid-cols-3 gap-3">
        {steps.map((s, i) => (
          <div key={s.tag} className="animate-fade-up" style={{ animationDelay: `${i * 120}ms` }}>
            <span className="text-[11px] font-semibold uppercase tracking-widest" style={{ color: s.color }}>{s.tag}</span>
            <div className="mt-1 flex flex-col justify-between rounded-lg border border-[#2A3655] bg-[#0B1020] p-2" style={{ aspectRatio: "3/4" }}>
              <div className="h-1.5 w-8 rounded-full" style={{ background: s.color }} />
              <div>
                <p className="text-xs font-semibold text-[#F8FAFC]">{s.label}</p>
                <p className="text-[10px] text-[#94A3B8]">{s.sub}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-center text-xs text-[#94A3B8]">Percorso <b className="text-[#CBD5E1]">Ho → Cerco → Scambio</b>. Composizione illustrativa, non uno scambio reale.</p>
    </div>
  );
}

const FEATURES = [
  { icon: Heart, title: "Condividi la tua passione", text: "Pubblica la tua collezione e mostra le carte che rendono unica la tua raccolta." },
  { icon: Users, title: "Confrontati con altri collezionisti", text: "Chiedi consigli, condividi esperienze e conosci persone che amano le carte come te." },
  { icon: Handshake, title: "Trova il prossimo scambio", text: "Il matching individua corrispondenze reali tra le vostre liste, senza vendite né denaro." },
];

const FAQ = [
  ["Omniverse è gratuito?", "Sì. Tutte le funzionalità della community e degli scambi sono gratuite. Non ci sono abbonamenti, commissioni o costi nascosti."],
  ["Posso vendere carte?", "No. Omniverse è dedicato esclusivamente agli scambi carta contro carta. Non gestiamo vendite, prezzi, valutazioni economiche o pagamenti."],
  ["Quali giochi sono supportati?", "La community accoglie appassionati di tutti i TCG. Il catalogo e il matching partono con Pokémon e One Piece; altri giochi potranno essere aggiunti in seguito."],
  ["Devo registrarmi per entrare nella community?", "Puoi esplorare il progetto e vedere le carte pubblicate anche come visitatore. Per gestire le tue liste o inviare proposte è necessario registrarsi (riservato ai maggiorenni)."],
  ["Come funziona un abbinamento?", "Un abbinamento reciproco esiste quando tu possiedi almeno una carta che l'altra persona cerca e viceversa. Ti mostriamo dati concreti come «Hai 3 carte che cerca» e «Ha 2 carte che cerchi», senza percentuali arbitrarie."],
  ["Omniverse garantisce gli scambi?", "No. Omniverse offre gli strumenti per incontrarsi e accordarsi, ma non custodisce né spedisce carte e non garantisce l'esito degli scambi. Scambia sempre con attenzione e prudenza."],
];

export default function Home() {
  const { user } = useAuth();
  return (
    <div className="space-y-20">
      {/* Hero */}
      <section className="relative -mx-4 -mt-6 overflow-hidden px-4 pt-10 sm:-mx-6 sm:px-6 sm:pt-16">
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative mx-auto grid max-w-container items-center gap-10 lg:grid-cols-2">
          <div className="animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#2A3655] bg-[#141B2D] px-3 py-1 text-xs text-[#2DD4BF]">
              <Sparkles className="h-3.5 w-3.5" /> Community gratuita per collezionisti
            </span>
            <h1 className="mt-5 font-display text-4xl font-bold leading-tight tracking-tight text-[#F8FAFC] sm:text-5xl lg:text-6xl">
              La passione per le carte.<br /><span className="text-[#7C3AED]">Le persone</span> con cui condividerla.
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-[#CBD5E1]">
              Entra in Omniverse, la community gratuita per chi ama le carte TCG. Condividi la tua collezione, chiedi consigli e trova altri appassionati con cui confrontarti e scambiare carte.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {user ? (
                <Button asChild size="lg" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Link to="/dashboard" data-testid="hero-cta-dashboard">Vai alla tua area <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              ) : (
                <Button asChild size="lg" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Link to="/registrati" data-testid="hero-cta-primary">Entra nella community <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
              )}
              <Button asChild size="lg" variant="outline" className="border-[#2A3655] bg-transparent text-[#F8FAFC] hover:bg-[#1C2640]"><Link to="/scambi" data-testid="hero-cta-secondary">Esplora gli scambi</Link></Button>
            </div>
            {!user && <p className="mt-4 text-sm text-[#94A3B8]">Iscrizione gratuita · Scambi tra collezionisti · Nessuna vendita</p>}
          </div>
          <div className="animate-fade-up" style={{ animationDelay: "150ms" }}><HoCercoScambio /></div>
        </div>
      </section>

      {/* A. Molto più di una carta */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Molto più di una carta</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="card-lift rounded-2xl border border-[#2A3655] bg-[#141B2D] p-6">
                <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#1C2640]"><Icon className="h-5 w-5 text-[#2DD4BF]" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold text-[#F8FAFC]">{f.title}</h3>
                <p className="mt-2 text-sm text-[#CBD5E1]">{f.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* B. Come funziona */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Come funziona</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {[[Layers, "1. Aggiungi le carte disponibili", "Inserisci le carte che possiedi e sei disposto a scambiare, con lingua e condizione."],
            [Heart, "2. Indica quelle che cerchi", "Crea la tua wishlist con priorità e preferenze di lingua e condizione."],
            [Repeat, "3. Trova corrispondenze e proponi", "Il sistema individua abbinamenti reciproci e puoi inviare una proposta di scambio."]].map(([Icon, t, d], i) => (
            <div key={t} className="rounded-2xl border border-[#2A3655] bg-[#141B2D] p-6">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[#7C3AED]/15"><Icon className="h-5 w-5 text-[#7C3AED]" /></div>
              <h3 className="mt-4 font-display text-lg font-semibold text-[#F8FAFC]">{t}</h3>
              <p className="mt-2 text-sm text-[#CBD5E1]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* C. Una community, tante passioni */}
      <section className="rounded-2xl border border-[#2A3655] bg-[#141B2D] p-8">
        <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Una community, tante passioni</h2>
        <p className="mt-3 max-w-3xl text-[#CBD5E1]">Omniverse accoglie appassionati di tutti i giochi di carte collezionabili: puoi parlare del tuo TCG preferito, mostrare le collezioni e confrontarti con altri.</p>
        <div className="mt-4 rounded-xl border border-[#2DD4BF]/30 bg-[#2DD4BF]/10 p-4">
          <p className="text-sm text-[#CBD5E1]"><b className="text-[#2DD4BF]">Catalogo e matching</b> partono con <b className="text-[#F8FAFC]">Pokémon</b> e <b className="text-[#F8FAFC]">One Piece</b>. Altri giochi potranno essere aggiunti in futuro.</p>
        </div>
      </section>

      {/* D. Telegram */}
      <section className="grid items-center gap-6 rounded-2xl border border-[#2A3655] bg-[#1C2640] p-8 md:grid-cols-[1fr_auto]">
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Le carte ci fanno incontrare</h2>
          <p className="mt-3 max-w-2xl text-[#CBD5E1]">La nostra community vive su Telegram: uno spazio per fare domande, mostrare le collezioni, discutere e partecipare insieme agli altri appassionati.</p>
        </div>
        <Button asChild size="lg" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Link to="/community" data-testid="home-community-cta"><MessageCircle className="mr-2 h-4 w-4" />Vai alla community</Link></Button>
      </section>

      {/* E. Scambiare con attenzione */}
      <section className="grid gap-6 md:grid-cols-[auto_1fr]">
        <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#F59E0B]/15"><ShieldCheck className="h-6 w-6 text-[#F59E0B]" /></div>
        <div>
          <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Scambiare con attenzione</h2>
          <p className="mt-3 max-w-3xl text-[#CBD5E1]">Usa fotografie reali delle tue carte, scrivi descrizioni accurate, verifica con cura le condizioni e agisci sempre con prudenza. Omniverse non garantisce gli scambi: l'accordo e la spedizione restano tra i collezionisti.</p>
        </div>
      </section>

      {/* F. FAQ */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-[#F8FAFC] sm:text-3xl">Domande frequenti</h2>
        <Accordion type="single" collapsible className="mt-6" data-testid="faq">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-[#2A3655]">
              <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-display text-[#F8FAFC] hover:text-[#2DD4BF]">{q}</AccordionTrigger>
              <AccordionContent className="text-[#CBD5E1]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* G. Invito finale */}
      {!user && (
      <section className="rounded-2xl border border-[#7C3AED]/40 bg-gradient-to-br from-[#1C2640] to-[#141B2D] p-10 text-center">
        <h2 className="font-display text-3xl font-bold text-[#F8FAFC]">Pronto a entrare in Omniverse?</h2>
        <p className="mx-auto mt-3 max-w-xl text-[#CBD5E1]">Unisciti a una community di appassionati e trova le persone giuste con cui condividere la passione per le carte.</p>
        <div className="mt-6 flex justify-center gap-3">
          <Button asChild size="lg" className="bg-[#7C3AED] hover:bg-[#6D28D9]"><Link to="/registrati" data-testid="home-final-cta">Entra nella community</Link></Button>
        </div>
      </section>
      )}
    </div>
  );
}
